import { type } from "arktype";
/**
 * Unified per-user progress slice — the "noun" half of the demo / tour /
 * lesson / onboarding / unlock / wizard family of systems.
 *
 * Design intent:
 *   - Three flat string-keyed maps, one per progress *kind*.
 *   - Keys are stable, versioned ids chosen by the call site
 *     (e.g. "wizard.connectBank.v1", "tour.netWorthTable.v1",
 *     "lesson.budgeting.intro", "feature.advancedReports").
 *   - Values are epoch-ms timestamps (`RooDate.now()`) recording when the
 *     event happened. A presence check is enough for read logic; the
 *     timestamp is there for analytics, debugging, and "what's new" UX.
 *
 * What does NOT belong here:
 *   - Step-graph definitions, predicates, or "flow" config. Each system
 *     owns its own UI tree; this slice only records outcomes.
 *   - Ephemeral UI state ("which page of the pager am I on right now").
 *     Use local component state for that.
 */
export const progressSchema = type({
    /** Things the user finished (wizards, lessons, tours, onboarding steps). */
    completed: "Record<string, number>",
    /** Things the user opted out of ("don't show this again"). */
    dismissed: "Record<string, number>",
    /** Features/capabilities the user has unlocked. */
    unlocked: "Record<string, number>",
});
/** The three progress kinds, as a runtime list so untrusted input can be checked against it. */
export const PROGRESS_KINDS = ["completed", "dismissed", "unlocked"];
/**
 * A kind is used as a property key on a plain `Progress` object, so an unchecked caller string like
 * "__proto__" or "constructor" would reach the process-wide prototypes. Gate on this list, never on a
 * truthy lookup (T-469).
 */
export function isProgressKind(x) {
    return typeof x === "string" && PROGRESS_KINDS.includes(x);
}
/** Returns a fresh, unshared Progress object with empty maps. */
export function createEmptyProgress() {
    return { completed: {}, dismissed: {}, unlocked: {} };
}
//# sourceMappingURL=progress.models.js.map